import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { addMonths, fmtMonth, HORIZON, NOW, toAbs } from "@/lib/months";
import { parseAmount, previewHandle } from "@/lib/format";
import { useStore, type NodeInput } from "@/model/store";
import type { Definition, Link } from "@/model/types";
import { IconPicker } from "@/components/NodeIcon";
import { DEFAULT_ICON, EVENT_ICONS, METRIC_ICONS } from "@/lib/icons";
import { ConditionEditor } from "./ConditionEditor";
import { sketchMonths, sketchRange, sketchWalk, type SketchPoint } from "@/lib/sketch";
import { cn } from "@/lib/utils";
import { MetricSketch } from "./MetricSketch";

type Kind = "event" | "metric";

/** The "New metric" steps: name it, then the range on the y-axis, the dates on the x-axis, then drag the line. */
const STEPS = ["Name", "Range", "Dates", "Shape"] as const;
const STEP_HINT = [
	"A number that changes over time, such as revenue, headcount or a rate.",
	"Roughly how low and how high could it go? A guess is fine.",
	"Over what time?",
	"Drag the dots to where you think it goes. You can fine-tune it later.",
];

const UNITS = [
	{ value: "USD", label: "Dollars" },
	{ value: "people", label: "People" },
	{ value: "%", label: "Percent" },
	{ value: "", label: "Number" },
];

export function NewNodeDialog({
	open,
	onOpenChange,
	kind: kind0,
	pos,
	kinds = ["event", "metric"],
	heading,
	connect,
	onCreated,
	initialTitle = "",
}: {
	open: boolean;
	onOpenChange: (v: boolean) => void;
	kind: Kind;
	pos?: { x: number; y: number };
	/** Kinds on offer (e.g. only events can drive a metric). */
	kinds?: Kind[];
	heading?: string;
	/** Create it linked to this node ("Add driver" / "Add link"): the API adds a default link in the same change. */
	connect?: { to: string; dir: "driver" | "dependent" };
	/** Called with the new node's handle (and the new link) instead of navigating to it. */
	onCreated?: (id: string, link?: Link) => void;
	/** Prefills the title (e.g. from a search that found nothing). */
	initialTitle?: string;
}) {
	const nodes = useStore((s) => s.world.nodes);
	const addNode = useStore((s) => s.addNode);
	const connectNode = useStore((s) => s.connectNode);
	const [busy, setBusy] = useState(false);
	const navigate = useNavigate();
	const [kind, setKind] = useState<Kind>(kind0);
	const [title, setTitle] = useState("");
	const [icon, setIcon] = useState<string>(DEFAULT_ICON.event);
	const [question, setQuestion] = useState("");
	const [criteria, setCriteria] = useState("");
	const [visibility, setVisibility] = useState<"private" | "public">("private");
	// Event
	const [how, setHow] = useState<"chance" | "derived">("chance");
	const [chance, setChance] = useState(20);
	const [byDate, setByDate] = useState("2027-12");
	const [def, setDef] = useState<Definition>({ combine: "all", when: [] });
	// Metric
	const [mHow, setMHow] = useState<"draw" | "constant" | "formula">("draw");
	const [unit, setUnit] = useState("USD");
	const [scale, setScale] = useState<"growth" | "level">("growth");
	const [current, setCurrent] = useState("");
	const [formula, setFormula] = useState("");
	// Metric, drawn
	const [step, setStep] = useState(0);
	const [low, setLow] = useState("");
	const [high, setHigh] = useState("");
	const [start, setStart] = useState(NOW);
	const [end, setEnd] = useState(NOW);
	const [points, setPoints] = useState<SketchPoint[] | null>(null);
	const [pointsKey, setPointsKey] = useState("");

	useEffect(() => {
		if (!open) return;
		setKind(kind0);
		setTitle(initialTitle);
		setQuestion("");
		setCriteria("");
		setHow("chance");
		setMHow("draw");
		setFormula("");
		setCurrent("");
		setStep(0);
		setLow("");
		setHigh("");
		setStart(NOW);
		setEnd(toAbs(addMonths(NOW, 12)) > toAbs(HORIZON) ? HORIZON : addMonths(NOW, 12));
		setPoints(null);
		setPointsKey("");
		setIcon(DEFAULT_ICON[kind0]);
		const firstMetric = Object.values(nodes).find((n) => n.kind === "metric");
		setDef({ combine: "all", when: firstMetric ? [{ metric: firstMetric.id, op: "<=", value: 0 }] : [] });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, kind0]);

	useEffect(() => {
		const list = kind === "event" ? EVENT_ICONS : METRIC_ICONS;
		if (!list.includes(icon)) setIcon(DEFAULT_ICON[kind]);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [kind]);

	// The API chooses the handle the same way (and adds a suffix if it is taken); this is the preview.
	const id = useMemo(() => previewHandle(title, nodes), [title, nodes]);

	// The formula is checked by the API as it is typed (`POST /formula/check` with no handle: a metric not created yet).
	const checkFormula = useStore((s) => s.checkFormula);
	const [check, setCheck] = useState<{ formula: string; error: string | null } | null>(null);
	const wantsCheck = kind === "metric" && mHow === "formula" && formula.trim().length > 0;
	useEffect(() => {
		if (!wantsCheck) return;
		let alive = true;
		const t = setTimeout(() => {
			void checkFormula(null, formula).then((r) => {
				if (!alive || !r) return;
				const error = r.error ?? (r.unknown.length ? `Unknown: ${r.unknown.join(", ")}.` : null) ?? r.issues[0]?.message ?? null;
				setCheck({ formula, error });
			});
		}, 250);
		return () => {
			alive = false;
			clearTimeout(t);
		};
	}, [wantsCheck, formula, checkFormula]);
	const checking = wantsCheck && check?.formula !== formula;
	const fErr = wantsCheck && check?.formula === formula ? check.error : null;
	// Unit-aware: "25B", "$2.5B", "150k", "5%" (review A6). NaN when it isn't a number.
	const num = (s: string) => parseAmount(s, unit) ?? NaN;

	const drawing = kind === "metric" && mHow === "draw";
	const lo = num(low);
	const hi = num(high);
	// Why each drawing step can't go on yet, in the same short line as Create's reason (review G3).
	const stepReason = !drawing
		? null
		: step === 0
			? title.trim().length === 0
				? "Add a title"
				: title.trim().length < 2
					? "Title needs two characters"
					: null
			: step === 1
				? !Number.isFinite(lo) || !Number.isFinite(hi)
					? low.trim() && high.trim()
						? "Use numbers such as 2.5M, 150k or 5%"
						: "Enter the lowest and highest values"
					: hi <= lo
						? "Highest must be above lowest"
						: null
				: step === 2
					? start < NOW
						? "Start now or later"
						: toAbs(end) - toAbs(start) < 2
							? "End at least two months after the start"
							: end > HORIZON
								? `End by ${fmtMonth(HORIZON)}`
								: null
					: null;

	function next() {
		if (stepReason) return;
		if (step === 2) {
			// A fresh line for new dates or a new range; the same line (and the person's drags) when coming back.
			const key = `${start}:${end}:${lo}:${hi}`;
			if (key !== pointsKey || !points) {
				setPoints(sketchWalk(sketchMonths(start, end), lo, hi));
				setPointsKey(key);
			}
		}
		setStep(step + 1);
	}

	const valid =
		title.trim().length > 1 &&
		(kind === "event"
			? how === "chance"
				? chance >= 0 && chance <= 99 && byDate > NOW
				: def.when.length > 0
			: mHow === "formula"
				? !!formula && !checking && !fErr
				: mHow === "constant"
					? Number.isFinite(num(current))
					: step === 3 && !!points);

	// Why Create is disabled, in one short line under the button (review G3).
	const reason = valid
		? null
		: title.trim().length === 0
			? "Add a title"
			: title.trim().length < 2
				? "Title needs two characters"
				: kind === "event"
					? how === "chance"
						? !(chance >= 0 && chance <= 99)
							? "Chance must be between 0% and 99%"
							: "Pick a date after now"
						: "Add at least one condition"
					: mHow === "formula"
						? formula
							? checking
								? "Checking the formula…"
								: "Fix the formula"
							: "Write a formula"
						: current.trim()
							? "The value isn’t a number"
							: "Enter a value";

	async function create() {
		const common = {
			title: title.trim(),
			icon,
			question: question.trim() || title.trim(),
			resolution: { criteria: criteria.trim(), source: "" },
			visibility,
		};
		let input: NodeInput;
		let observed: { month: string; value: number }[] = [];
		if (kind === "event") {
			input = {
				...common,
				kind: "event",
				hero_date: byDate,
				...(how === "derived" ? { definition: def } : { forecast: { by: { [byDate]: chance / 100 } } }),
			};
		} else {
			const c = num(current);
			let range: Record<string, [number, number, number]> = {};
			if (mHow === "draw") {
				range = sketchRange(points!, lo, hi);
				// A dot at "now" is what it is today: an actual, not a forecast.
				if (range[NOW]) {
					observed = [{ month: NOW, value: range[NOW][1] }];
					delete range[NOW];
				}
			} else if (mHow === "constant") {
				range = { "2027-12": [c, c, c] };
				observed = [{ month: NOW, value: c }];
			}
			input = {
				...common,
				kind: "metric",
				unit,
				// Changes multiply only for amounts that stay above zero (revenue, prices); anything that can reach zero adds.
				scale: mHow === "draw" ? (unit === "USD" && lo > 0 ? "growth" : "level") : scale,
				hero_date: mHow === "draw" ? end : "2027-12",
				forecast: mHow === "formula" ? { formula } : { range },
			};
		}
		setBusy(true);
		try {
			if (connect) {
				const r = await connectNode(connect.to, connect.dir, { new_node: input }, pos);
				if (!r) return;
				if (observed.length) await useStore.getState().importObservations(r.node, observed);
				onOpenChange(false);
				if (onCreated) onCreated(r.node, r.link);
				else navigate(`/node/${r.node}`);
				return;
			}
			const handle = await addNode(input, { pos, observed });
			if (!handle) return;
			onOpenChange(false);
			if (onCreated) onCreated(handle);
			else navigate(`/node/${handle}`);
		} finally {
			setBusy(false);
		}
	}

	const kindToggle = kinds.length > 1 && (
		<ToggleGroup type="single" variant="outline" value={kind} onValueChange={(v) => v && setKind(v as Kind)} className="justify-start">
			{kinds.includes("event") && <ToggleGroupItem value="event">Event</ToggleGroupItem>}
			{kinds.includes("metric") && <ToggleGroupItem value="metric">Metric</ToggleGroupItem>}
		</ToggleGroup>
	);
	const iconAndTitle = (big: boolean) => (
		<div className="grid grid-cols-[auto_1fr] gap-3">
			<div className="space-y-1.5">
				<Label>Icon</Label>
				<IconPicker value={icon} onChange={setIcon} kind={kind} />
			</div>
			<div className="space-y-1.5">
				<Label htmlFor="nn-title">Title</Label>
				<Input
					id="nn-title"
					className={cn(big && "h-11 text-lg")}
					value={title}
					onChange={(e) => setTitle(e.target.value)}
					placeholder={kind === "event" ? "Globex renews early" : "Pipeline value"}
					autoFocus
				/>
				{title && (
					<p className="text-xs text-muted-foreground">
						Formula name: <code>{id}</code>
					</p>
				)}
			</div>
		</div>
	);
	const visibilityToggle = (
		<ToggleGroup type="single" variant="outline" value={visibility} onValueChange={(v) => v && setVisibility(v as "private" | "public")}>
			<ToggleGroupItem value="private">Private</ToggleGroupItem>
			<ToggleGroupItem value="public">Public</ToggleGroupItem>
		</ToggleGroup>
	);
	const footerReason = drawing && step < 3 ? stepReason : reason;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
				<DialogHeader>
					<DialogTitle>{heading ?? `New ${kind}`}</DialogTitle>
					<DialogDescription>
						{kind === "event" ? "Something that happens at some point, or never." : drawing ? STEP_HINT[step] : STEP_HINT[0]}
					</DialogDescription>
				</DialogHeader>

				{drawing ? (
					<form
						className="space-y-4"
						onSubmit={(e) => {
							e.preventDefault();
							if (step < 3) next();
							else if (valid && !busy) void create();
						}}
					>
						<ol className="flex items-center gap-2 text-xs" aria-label="Steps">
							{STEPS.map((s, i) => (
								<li key={s} className="flex items-center gap-2" aria-current={i === step ? "step" : undefined}>
									{i > 0 && <span className={cn("h-px w-6", i <= step ? "bg-foreground/40" : "bg-border")} />}
									<span
										className={cn(
											"flex size-5 items-center justify-center rounded-full border text-[11px] tabular-nums",
											i < step && "border-foreground/40 bg-foreground/10",
											i === step && "border-[var(--brand-periwinkle-deeper)] bg-primary font-medium",
										)}
									>
										{i + 1}
									</span>
									<span className={cn(i === step ? "font-medium text-foreground" : "text-muted-foreground")}>{s}</span>
								</li>
							))}
						</ol>

						{step === 0 ? (
							<div className="space-y-4">
								{kindToggle}
								{iconAndTitle(true)}
								<div className="space-y-1.5">
									<Label>Measured in</Label>
									{/* Radix reads an empty value as "nothing selected", so "Number" (unit "") is " " in the toggle only. */}
									<ToggleGroup type="single" variant="outline" value={unit || " "} onValueChange={(v) => v && setUnit(v.trim())} className="justify-start">
										{UNITS.map((u) => (
											<ToggleGroupItem key={u.label} value={u.value || " "}>
												{u.label}
											</ToggleGroupItem>
										))}
									</ToggleGroup>
								</div>
								<div className="flex flex-wrap items-center justify-between gap-3 text-sm">
									{visibilityToggle}
									<span className="text-xs text-muted-foreground">
										Is it a fixed value or a formula?{" "}
										<button type="button" className="underline underline-offset-2 hover:text-foreground" onClick={() => setMHow("constant")}>
											Set it directly
										</button>
									</span>
								</div>
							</div>
						) : (
							<MetricSketch
								stage={step === 1 ? "range" : step === 2 ? "dates" : "shape"}
								unit={unit}
								low={low}
								high={high}
								onLow={setLow}
								onHigh={setHigh}
								start={start}
								end={end}
								onStart={setStart}
								onEnd={setEnd}
								points={points}
								onPoints={setPoints}
							/>
						)}

						<DialogFooter className="sm:items-start">
							{step === 0 ? (
								<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
									Cancel
								</Button>
							) : (
								<Button type="button" variant="ghost" onClick={() => setStep(step - 1)}>
									Back
								</Button>
							)}
							{step === 3 && (
								<Button type="button" variant="outline" onClick={() => setPoints(sketchWalk(sketchMonths(start, end), lo, hi))}>
									Try another line
								</Button>
							)}
							<div className="flex flex-col items-stretch gap-1 sm:items-end">
								{step < 3 ? (
									<Button type="submit" disabled={!!stepReason} aria-describedby={stepReason ? "nn-reason" : undefined}>
										Next
									</Button>
								) : (
									<Button type="submit" disabled={!valid || busy} aria-describedby={reason ? "nn-reason" : undefined}>
										{connect ? "Create metric and link it" : "Create metric"}
									</Button>
								)}
								{footerReason && (
									<p id="nn-reason" className="text-center text-xs text-muted-foreground sm:text-right">
										{footerReason}
									</p>
								)}
							</div>
						</DialogFooter>
					</form>
				) : (
					<>
						{kindToggle}
						{iconAndTitle(false)}
						{kind === "event" && (
							<>
								<div className="space-y-1.5">
									<Label htmlFor="nn-q">Question</Label>
									<Input
										id="nn-q"
										value={question}
										onChange={(e) => setQuestion(e.target.value)}
										placeholder="When will Globex sign a renewal before the current term ends?"
									/>
								</div>
								<div className="grid grid-cols-[1fr_auto] gap-3">
									<div className="space-y-1.5">
										<Label htmlFor="nn-r">How it’s decided</Label>
										<Input id="nn-r" value={criteria} onChange={(e) => setCriteria(e.target.value)} placeholder="Checkable criteria and a named source" />
									</div>
									<div className="space-y-1.5">
										<Label>Visibility</Label>
										{visibilityToggle}
									</div>
								</div>
							</>
						)}

						{kind === "event" ? (
							<div className="space-y-3 rounded-lg border bg-muted/40 p-4">
								<ToggleGroup
									type="single"
									variant="outline"
									value={how}
									onValueChange={(v) => v && setHow(v as "chance" | "derived")}
									className="justify-start"
								>
									<ToggleGroupItem value="chance">Give a chance</ToggleGroupItem>
									<ToggleGroupItem value="derived">Calculate it from a condition</ToggleGroupItem>
								</ToggleGroup>
								{how === "chance" ? (
									<div className="flex flex-wrap items-center gap-2 text-sm">
										<Input
											type="number"
											className="w-20"
											min={0}
											max={99}
											value={chance}
											onChange={(e) => setChance(Number(e.target.value))}
											aria-label="Chance"
										/>
										% chance by
										<Input type="month" className="w-44" min="2026-10" value={byDate} onChange={(e) => setByDate(e.target.value)} aria-label="Date" />
										<span className="text-muted-foreground">The curve can be dragged into shape next.</span>
									</div>
								) : (
									<ConditionEditor value={def} onChange={setDef} nodes={Object.values(nodes)} />
								)}
							</div>
						) : (
							<div className="space-y-3 rounded-lg border bg-muted/40 p-4">
								<div className="flex flex-wrap gap-3">
									<ToggleGroup type="single" variant="outline" value={mHow} onValueChange={(v) => v && setMHow(v as typeof mHow)} className="justify-start">
										<ToggleGroupItem value="draw">Draw it</ToggleGroupItem>
										<ToggleGroupItem value="constant">Constant</ToggleGroupItem>
										<ToggleGroupItem value="formula">Formula</ToggleGroupItem>
									</ToggleGroup>
									<Select value={unit} onValueChange={setUnit}>
										<SelectTrigger className="w-32">
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="USD">Dollars</SelectItem>
											<SelectItem value="people">People</SelectItem>
											<SelectItem value="%">Percent</SelectItem>
											<SelectItem value="x">Multiple (×)</SelectItem>
											<SelectItem value="">Number</SelectItem>
										</SelectContent>
									</Select>
									<Select value={scale} onValueChange={(v) => setScale(v as "growth" | "level")}>
										<SelectTrigger className="w-72">
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="growth">Changes multiply (revenue, prices)</SelectItem>
											<SelectItem value="level">Changes add (headcount, rates)</SelectItem>
										</SelectContent>
									</Select>
								</div>
								{mHow === "formula" ? (
									<div className="space-y-1.5">
										<Textarea
											className="font-mono text-sm"
											value={formula}
											onChange={(e) => setFormula(e.target.value)}
											placeholder="acme_revenue + globex_revenue"
										/>
										<p className={"text-xs " + (fErr ? "text-[var(--risk-ink)]" : "text-muted-foreground")}>
											{fErr ?? "Use formula names, + − × ÷, min, max, floor, ceil, round, sum, lag(metric, months), happened(event)."}
										</p>
									</div>
								) : (
									<div className="flex items-end gap-3 text-sm">
										<NumField label="Value" value={current} onChange={setCurrent} unit={unit} />
										<p className="pb-2 text-xs text-muted-foreground">The same in every period, e.g. a target margin or a price.</p>
									</div>
								)}
							</div>
						)}

						<DialogFooter className="sm:items-start">
							<Button variant="ghost" onClick={() => onOpenChange(false)}>
								Cancel
							</Button>
							<div className="flex flex-col items-stretch gap-1 sm:items-end">
								<Button disabled={!valid || busy} onClick={() => void create()} aria-describedby={reason ? "nn-reason" : undefined}>
									{connect ? `Create ${kind} and link it` : `Create ${kind}`}
								</Button>
								{reason && (
									<p id="nn-reason" className="text-center text-xs text-muted-foreground sm:text-right">
										{reason}
									</p>
								)}
							</div>
						</DialogFooter>
					</>
				)}
			</DialogContent>
		</Dialog>
	);
}

function NumField({ label, value, onChange, unit }: { label: string; value: string; onChange: (v: string) => void; unit: string }) {
	return (
		<div className="space-y-1.5">
			<Label className="text-xs">
				{label} {unit === "%" ? "(%)" : unit === "USD" ? "($)" : ""}
			</Label>
			<Input
				className="w-32"
				inputMode="decimal"
				value={value}
				onChange={(e) => onChange(e.target.value)}
				placeholder={unit === "USD" ? "$2.5M" : unit === "%" ? "5%" : ""}
			/>
		</div>
	);
}
